import {
    COMPONENT_XML_TEMPLATE,
    SCREEN_XML_TEMPLATE,
    TEST_XML_TEMPLATE,
    WIDGET_XML_TEMPLATE,
    INVALID_C_IDENTIFIER_MESSAGE,
    isValidCIdentifier,
    normalizeFilePathForComparison,
    XML_FILE_EXTENSION
} from '@lved/shared-core';
import { FileNavigatorWidget, FILE_NAVIGATOR_ID } from '@theia/navigator/lib/browser/navigator-widget';
import { LabelProvider, open, OpenerService, SelectableTreeNode } from '@theia/core/lib/browser';
import { WidgetManager } from '@theia/core/lib/browser/widget-manager';
import { MessageService } from '@theia/core/lib/common/message-service';
import URI from '@theia/core/lib/common/uri';
import { inject, injectable } from '@theia/core/shared/inversify';
import { DirNode } from '@theia/filesystem/lib/browser';
import { FileService } from '@theia/filesystem/lib/browser/file-service';
import { WorkspaceInputDialog } from '@theia/workspace/lib/browser/workspace-input-dialog';
import { WorkspaceService } from '@theia/workspace/lib/browser/workspace-service';
import { FileStat } from '@theia/filesystem/lib/common/files';
import { TEMPLATE_ROOTS, TemplateRoot } from './template-create-commands';

const TEMPLATE_BY_ROOT: Record<TemplateRoot, string> = {
    components: COMPONENT_XML_TEMPLATE,
    screens: SCREEN_XML_TEMPLATE,
    widgets: WIDGET_XML_TEMPLATE,
    tests: TEST_XML_TEMPLATE
};

const DIALOG_TITLE_BY_ROOT: Record<TemplateRoot, string> = {
    components: 'New Component',
    screens: 'New Screen',
    widgets: 'New Widget',
    tests: 'New Test'
};

@injectable()
export class TemplateCreateService {
    @inject(FileService)
    protected readonly fileService!: FileService;

    @inject(WorkspaceService)
    protected readonly workspaceService!: WorkspaceService;

    @inject(WidgetManager)
    protected readonly widgetManager!: WidgetManager;

    @inject(LabelProvider)
    protected readonly labelProvider!: LabelProvider;

    @inject(MessageService)
    protected readonly messageService!: MessageService;

    @inject(OpenerService)
    protected readonly openerService!: OpenerService;

    getTemplateRootForUri(uri: URI): TemplateRoot | undefined {
        const workspaceRoot: URI | undefined = this.getWorkspaceRootUri();
        if (!workspaceRoot) return undefined;

        const workspacePath: string = normalizeFilePathForComparison(workspaceRoot.path.toString());
        const targetPath: string = normalizeFilePathForComparison(uri.path.toString());
        if (!targetPath.startsWith(workspacePath)) return undefined;

        const relativePath: string = targetPath.slice(workspacePath.length).replace(/^\/+/, '');
        const rootName: string = relativePath.split('/')[0];
        if (TEMPLATE_ROOTS.includes(rootName as TemplateRoot)) return rootName as TemplateRoot;

        return undefined;
    }

    getTemplateRootForSelection(): TemplateRoot | undefined {
        const selectedUri: URI | undefined = this.getSelectedFolderUri();
        return selectedUri ? this.getTemplateRootForUri(selectedUri) : undefined;
    }

    resolveParentUri(templateRoot: TemplateRoot, explicitParent?: URI): URI | undefined {
        if (explicitParent) return explicitParent;

        const selectedUri: URI | undefined = this.getSelectedFolderUri();
        if (selectedUri && this.getTemplateRootForUri(selectedUri) === templateRoot) return selectedUri;

        const workspaceRoot: URI | undefined = this.getWorkspaceRootUri();
        if (!workspaceRoot) return undefined;

        return workspaceRoot.resolve(templateRoot);
    }

    canCreate(templateRoot: TemplateRoot, explicitParent?: URI): boolean {
        return !!this.resolveParentUri(templateRoot, explicitParent);
    }

    async create(templateRoot: TemplateRoot, explicitParent?: URI): Promise<void> {
        if (!this.workspaceService.opened) {
            await this.messageService.error('Open a project folder before creating resources.');
            return;
        }

        const parentUri: URI | undefined = this.resolveParentUri(templateRoot, explicitParent);
        if (!parentUri) {
            await this.messageService.error('Could not resolve the target folder for this resource.');
            return;
        }

        let parentStat: FileStat | undefined;
        try {
            parentStat = await this.fileService.resolve(parentUri);
        } catch {
            await this.messageService.error(`Folder "${parentUri.path.base}" does not exist.`);
            return;
        }

        if (!parentStat.isDirectory) {
            await this.messageService.error('The selected target is not a folder.');
            return;
        }

        const dialog: WorkspaceInputDialog = new WorkspaceInputDialog(
            {
                title: DIALOG_TITLE_BY_ROOT[templateRoot],
                maxWidth: 400,
                parentUri,
                initialValue: '',
                placeholder: 'Name',
                validate: (name: string) => this.validateName(name)
            },
            this.labelProvider
        );

        const enteredName: string | undefined = await dialog.open();
        if (!enteredName) return;

        const trimmedName: string = enteredName.trim();
        const baseName: string = trimmedName.endsWith(XML_FILE_EXTENSION)
            ? trimmedName.slice(0, -XML_FILE_EXTENSION.length)
            : trimmedName;

        const validationError: string | boolean = this.validateName(baseName);
        if (typeof validationError === 'string') {
            await this.messageService.error(validationError);
            return;
        }

        const folderUri: URI = parentUri.resolve(baseName);
        const fileUri: URI = folderUri.resolve(`${baseName}${XML_FILE_EXTENSION}`);

        try {
            if (!(await this.fileService.exists(folderUri))) await this.fileService.createFolder(folderUri);
            await this.fileService.create(fileUri, TEMPLATE_BY_ROOT[templateRoot], { overwrite: false });
        } catch (error: unknown) {
            const message: string = error instanceof Error ? error.message : String(error);
            await this.messageService.error(`Failed to create ${templateRoot.slice(0, -1)}: ${message}`);
            return;
        }

        await this.expandParentFolder(parentUri);
        await open(this.openerService, fileUri);
    }

    protected validateName(name: string): string | boolean {
        const trimmed: string = name.trim();
        if (!trimmed) return 'Name cannot be empty.';

        const baseName: string = trimmed.endsWith(XML_FILE_EXTENSION)
            ? trimmed.slice(0, -XML_FILE_EXTENSION.length)
            : trimmed;

        if (!baseName || !isValidCIdentifier(baseName)) {
            return `Invalid name "${baseName || 'empty'}". ${INVALID_C_IDENTIFIER_MESSAGE}`;
        }

        return true;
    }

    protected getWorkspaceRootUri(): URI | undefined {
        return this.workspaceService.tryGetRoots()[0]?.resource;
    }

    protected getSelectedFolderUri(): URI | undefined {
        const widget: FileNavigatorWidget | undefined =
            this.widgetManager.tryGetWidget<FileNavigatorWidget>(FILE_NAVIGATOR_ID);
        const selectedNode: Readonly<SelectableTreeNode> | undefined = widget?.model.selectedNodes[0];
        if (!selectedNode || !DirNode.is(selectedNode)) return undefined;

        return selectedNode.uri;
    }

    protected async expandParentFolder(parentUri: URI): Promise<void> {
        const widget: FileNavigatorWidget | undefined =
            await this.widgetManager.getWidget<FileNavigatorWidget>(FILE_NAVIGATOR_ID);
        const node: Readonly<SelectableTreeNode> | undefined = widget?.model.getNodesByUri(parentUri).next().value;
        if (node && DirNode.is(node)) widget?.model.expandNode(node);
    }
}
